// Reference-counted body-scroll-lock. Multiple modal surfaces (the
// story slide-out, the settings drawer, future surfaces) can hold the
// lock simultaneously without stomping each other's captured `prev`:
// the first push sets `body.overflow = "hidden"` and remembers what
// was there; subsequent pushes only bump the counter; pops decrement
// and the last pop restores the original value.
//
// Before this module each surface captured `body.style.overflow` on
// mount and restored it on unmount independently. If A mounted, then
// B mounted, B captured the already-"hidden" value as `prev`, and on
// unmount B restored "hidden" — leaking the lock for A's lifetime.
// Order-dependent and brittle. Refcount makes the semantics local to
// the module and contract-checkable by tests.

let lockCount = 0;
let previousOverflow = "";

/** Acquire the body-scroll-lock. First caller flips `body.overflow`;
 *  subsequent callers only bump the counter. Returns a release fn —
 *  call it (idempotent under double-call) when the surface unmounts. */
export function pushBodyScrollLock(): () => void {
  if (lockCount === 0) {
    previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
  }
  lockCount += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    lockCount -= 1;
    if (lockCount === 0) {
      document.body.style.overflow = previousOverflow;
      previousOverflow = "";
    }
  };
}

/** Test-only: reset the internal state. Used by `bodyScrollLock.test.ts`
 *  between tests; never called from production code. */
export function __resetBodyScrollLock(): void {
  lockCount = 0;
  previousOverflow = "";
}
